/**
 * Hotspot map state ⇄ URL. The map lives on the Findings page as
 * `?tab=hotspots&by=capability&window=last5&hotspot=<key>`; the filter
 * params are the queue's (`persona`, `cap`, `sev`, …), so one filter
 * applies to both views.
 */
import {
  DEFAULT_HOTSPOT_WINDOW,
  formatHotspotWindow,
  HOTSPOT_KEYS,
  HOTSPOT_PRESSURE_STATES,
  type HotspotBy,
  type HotspotFilters,
  hotspotFiltersSchema,
  type HotspotWindow,
  parseHotspotWindow,
} from "@iamu/shared";
import { parseQueueUrl } from "@/components/triage/queue-url-state";

export type HotspotLayout = "list" | "matrix";
/**
 * `rank` is the default "weakest first" order from `compareHotspotRows`
 * (failing human tests, then regressions, then pressure). It is not a
 * column, so no header claims it; the others sort by that column's value.
 */
export type HotspotSortCol =
  | "rank"
  | "area"
  | "pressure"
  | "open"
  | "outcome"
  | "runs"
  | "steps";
export type HotspotSortDir = "ascending" | "descending";

export interface HotspotUrlState {
  by: HotspotBy;
  window: HotspotWindow;
  /** Hotspot open in the panel. */
  key: string | null;
  includeSecondary: boolean;
  layout: HotspotLayout;
  filters: HotspotFilters;
  sortCol: HotspotSortCol;
  sortDir: HotspotSortDir;
}

/** Params owned by the hotspot view (dropped when going back to Queue). */
export const HOTSPOT_PARAMS: readonly string[] = [
  "tab",
  "by",
  "window",
  "hotspot",
  "secondary",
  "layout",
  "sort",
  "dir",
];

const VALID_SORT_COLS = new Set<string>([
  "rank",
  "area",
  "pressure",
  "open",
  "outcome",
  "runs",
  "steps",
]);

export function isHotspotTab(params: URLSearchParams): boolean {
  return params.get("tab") === "hotspots";
}

export function parseHotspotUrl(params: URLSearchParams): HotspotUrlState {
  const by = params.get("by");
  const filters = hotspotFiltersSchema.safeParse(parseQueueUrl(params).query);
  const rawSort = params.get("sort");
  return {
    by: (HOTSPOT_KEYS as readonly string[]).includes(by ?? "")
      ? (by as HotspotBy)
      : "capability",
    window: runsWindow(parseHotspotWindow(params.get("window"))),
    key: params.get("hotspot"),
    includeSecondary: params.get("secondary") === "1",
    layout: params.get("layout") === "matrix" ? "matrix" : "list",
    filters: filters.success ? stripEmpty(filters.data) : {},
    sortCol:
      rawSort && VALID_SORT_COLS.has(rawSort)
        ? (rawSort as HotspotSortCol)
        : "rank",
    sortDir: params.get("dir") === "ascending" ? "ascending" : "descending",
  };
}

/**
 * The map's own window is runs per scenario only; a days window is the
 * page scope's period now (`last=30d`, read by `parsePeriod`).
 */
function runsWindow(w: HotspotWindow): HotspotWindow {
  return w.kind === "days" ? { kind: "all" } : w;
}

function stripEmpty(f: HotspotFilters): HotspotFilters {
  return Object.fromEntries(
    Object.entries(f).filter(([, v]) => v !== undefined),
  ) as HotspotFilters;
}

/** The current params with the hotspot state patched in. */
export function withHotspotState(
  params: URLSearchParams,
  patch: Partial<Omit<HotspotUrlState, "filters">>,
): URLSearchParams {
  const p = new URLSearchParams(params);
  p.set("tab", "hotspots");
  const set = (name: string, value: string | null) =>
    value ? p.set(name, value) : p.delete(name);
  if (patch.by !== undefined)
    set("by", patch.by === "capability" ? null : patch.by);
  if (patch.window !== undefined) {
    const w = formatHotspotWindow(patch.window);
    set("window", w === formatHotspotWindow(DEFAULT_HOTSPOT_WINDOW) ? null : w);
  }
  if (patch.key !== undefined) set("hotspot", patch.key);
  if (patch.includeSecondary !== undefined) {
    set("secondary", patch.includeSecondary ? "1" : null);
  }
  if (patch.layout !== undefined) {
    set("layout", patch.layout === "matrix" ? "matrix" : null);
  }
  if (patch.sortCol !== undefined) {
    set("sort", patch.sortCol === "rank" ? null : patch.sortCol);
  }
  if (patch.sortDir !== undefined) {
    set("dir", patch.sortDir === "descending" ? null : patch.sortDir);
  }
  return p;
}

/** The current params for the Queue view (hotspot params dropped). */
export function queueParams(params: URLSearchParams): URLSearchParams {
  const p = new URLSearchParams(params);
  for (const name of HOTSPOT_PARAMS) p.delete(name);
  if (p.get("view") === "queue") p.delete("view");
  return p;
}

const findingsBase = (projectId: string) => `/projects/${projectId}/findings`;

/** Link into the map, optionally with a hotspot open. */
export function hotspotHref(
  projectId: string,
  opts: { by?: HotspotBy; key?: string | null } = {},
): string {
  const p = withHotspotState(new URLSearchParams(), {
    by: opts.by,
    key: opts.key ?? null,
  });
  return `${findingsBase(projectId)}?${p}`;
}

/** The triage queue filtered to an area's unresolved problems. */
export function queueAreaHref(projectId: string, key: string): string {
  const p = new URLSearchParams({
    state: HOTSPOT_PRESSURE_STATES.join(","),
    area: key,
  });
  return `${findingsBase(projectId)}?${p}`;
}
